export interface GrayscaleDisplacementMap {
  width: number;
  height: number;
  values: readonly number[];
}

export interface DisplaceSettings {
  horizontalScale: number;
  verticalScale: number;
  mode: 'fit' | 'tile';
}

function wrap(value: number, limit: number): number {
  return ((value % limit) + limit) % limit;
}

function mapSample(map: GrayscaleDisplacementMap, x: number, y: number, mode: DisplaceSettings['mode']): number {
  const coordinate = (value: number, limit: number) => mode === 'tile' ? wrap(value, limit) : Math.max(0, Math.min(limit - 1, value));
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const amountX = x - x0;
  const amountY = y - y0;
  const value = (sampleX: number, sampleY: number) => map.values[Math.round(coordinate(sampleY, map.height)) * map.width + Math.round(coordinate(sampleX, map.width))] ?? 128;
  const top = value(x0, y0) * (1 - amountX) + value(x1, y0) * amountX;
  const bottom = value(x0, y1) * (1 - amountX) + value(x1, y1) * amountX;
  return top * (1 - amountY) + bottom * amountY;
}

function pixelSample(source: Uint8ClampedArray, width: number, height: number, x: number, y: number, channel: number): number {
  const clampedX = Math.max(0, Math.min(width - 1, x));
  const clampedY = Math.max(0, Math.min(height - 1, y));
  const x0 = Math.floor(clampedX);
  const y0 = Math.floor(clampedY);
  const x1 = Math.min(width - 1, x0 + 1);
  const y1 = Math.min(height - 1, y0 + 1);
  const amountX = clampedX - x0;
  const amountY = clampedY - y0;
  const value = (sampleX: number, sampleY: number) => source[(sampleY * width + sampleX) * 4 + channel] ?? 0;
  const top = value(x0, y0) * (1 - amountX) + value(x1, y0) * amountX;
  const bottom = value(x0, y1) * (1 - amountX) + value(x1, y1) * amountX;
  return top * (1 - amountY) + bottom * amountY;
}

export function applyDisplacePixels(
  source: Uint8ClampedArray,
  width: number,
  height: number,
  map: GrayscaleDisplacementMap,
  settings: DisplaceSettings,
): Uint8ClampedArray {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || source.length !== width * height * 4) throw new Error('Displace source dimensions are invalid.');
  if (!Number.isInteger(map.width) || !Number.isInteger(map.height) || map.width < 1 || map.height < 1 || map.values.length !== map.width * map.height) throw new Error('Displacement map dimensions are invalid.');
  const output = new Uint8ClampedArray(source.length);
  const horizontalScale = Math.max(-1_000, Math.min(1_000, settings.horizontalScale));
  const verticalScale = Math.max(-1_000, Math.min(1_000, settings.verticalScale));
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const mapX = settings.mode === 'fit' ? (x + 0.5) / width * map.width - 0.5 : x;
    const mapY = settings.mode === 'fit' ? (y + 0.5) / height * map.height - 0.5 : y;
    const mapValue = mapSample(map, mapX, mapY, settings.mode);
    const displacement = mapValue >= 128 ? (mapValue - 128) / 127 : (mapValue - 128) / 128;
    const sourceX = x + displacement * horizontalScale;
    const sourceY = y + displacement * verticalScale;
    const offset = (y * width + x) * 4;
    for (let channel = 0; channel < 4; channel += 1) output[offset + channel] = Math.round(pixelSample(source, width, height, sourceX, sourceY, channel));
  }
  return output;
}
